var LogLevel;
(function (LogLevel) {
    LogLevel[LogLevel["Debug"] = 0] = "Debug";
    LogLevel[LogLevel["Info"] = 1] = "Info";
    LogLevel[LogLevel["Warn"] = 2] = "Warn";
    LogLevel[LogLevel["Error"] = 3] = "Error";
    LogLevel[LogLevel["Silent"] = 4] = "Silent";
})(LogLevel || (LogLevel = {}));
function setLogLevel(level) {
    if (typeof level === "number") {
        Logger.LEVEL = level;
    }
    else {
        switch (level) {
            case "debug":
                Logger.LEVEL = LogLevel.Debug;
                break;
            case "info":
                Logger.LEVEL = LogLevel.Info;
                break;
            case "warn":
                Logger.LEVEL = LogLevel.Warn;
                break;
            case "error":
                Logger.LEVEL = LogLevel.Error;
                break;
            case "silent":
                Logger.LEVEL = LogLevel.Silent;
                break;
            default:
                throw new TypeError(`Invalid log level "${level}"`);
        }
    }
}
class Logger {
    static create(prefix) {
        if (prefix) {
            return new Logger(`yggdrasil ${prefix}`);
        }
        else {
            return new Logger("yggdrasil");
        }
    }
    constructor(prefix) {
        this.prefix = prefix;
    }
    debug(...parts) {
        if (Logger.LEVEL <= LogLevel.Debug) {
            console.debug(`[${this.prefix}]`, ...parts);
        }
    }
    info(...parts) {
        if (Logger.LEVEL <= LogLevel.Info) {
            console.info(`[${this.prefix}]`, ...parts);
        }
    }
    warn(...parts) {
        if (Logger.LEVEL <= LogLevel.Warn) {
            console.warn(`[${this.prefix}]`, ...parts);
        }
    }
    error(...parts) {
        if (Logger.LEVEL <= LogLevel.Error) {
            console.error(`[${this.prefix}]`, ...parts);
        }
    }
}
Logger.LEVEL = LogLevel.Info;

class PageObserver extends EventTarget {
    constructor(pageObserverOptions) {
        super();
        this.pageObserverOptions = pageObserverOptions;
    }
    dispatchPageChangeEvent(page) {
        if (this.isIgnored(page)) {
            this.log.debug(`Page "${page}" is ignored"`);
            return;
        }
        const event = new CustomEvent("pagechange", {
            detail: {
                page,
            },
        });
        this.dispatchEvent(event);
    }
    dispatchUrlChangeEvent(path, hash, search) {
        const eventData = {
            path,
            search,
            hash,
        };
        const event = new CustomEvent("urlchange", {
            detail: eventData,
        });
        this.dispatchEvent(event);
    }
    dispatchPageEndEvent() {
        const event = new CustomEvent("pageend");
        this.dispatchEvent(event);
    }
    addEventListener(type, callback, options) {
        super.addEventListener(type, callback, options);
    }
    isIgnored(page) {
        const ignoreList = this.pageObserverOptions?.ignore ?? [];
        return ignoreList.some((ignored) => {
            if (typeof ignored === "string") {
                return page === ignored;
            }
            else {
                return page.match(ignored);
            }
        });
    }
}
class Observer {
    constructor(callback) {
        this.callback = callback;
    }
    triggerCallback(record) {
        try {
            this.callback(record, this);
        }
        catch (error) {
            this.log.error("Error in callback:", error);
        }
    }
}

class CompanyonPageObserver extends PageObserver {
    constructor(options) {
        super(options);
        this.log = Logger.create("CompanyonPageObserver");
        this.messageType = options.messageType || "setShortcutContext";
        this.messageProperty = options.messageProperty || "name";
    }
    start() {
        try {
            let oldPage;
            infor.companyon.client.registerMessageHandler(this.messageType, (msg) => {
                const newPage = msg[this.messageProperty];
                if (typeof newPage === "string" && newPage !== oldPage) {
                    oldPage = newPage;
                    this.dispatchPageEndEvent();
                    this.dispatchPageChangeEvent(newPage);
                }
            });
        }
        catch (err) {
            this.log.error("Failed to track page with companyon client:", err);
        }
    }
}

class ElementPageObserver extends PageObserver {
    constructor(options) {
        super(options);
        this.options = options;
        this.log = Logger.create("ElementPageObserver");
        this.log.debug(`created with selector "${this.options.selector}"`);
    }
    start() {
        const element = this.findElement();
        if (!element) {
            throw new Error(`Could not find element with selector ${this.options.selector}`);
        }
        let oldPage = this.getName(element);
        this.dispatchPageChangeEvent(oldPage);
        const observer = new MutationObserver(() => {
            const newPage = this.getName(element);
            if (newPage && newPage !== oldPage) {
                this.dispatchPageEndEvent();
                this.dispatchPageChangeEvent(newPage);
                oldPage = newPage;
            }
        });
        observer.observe(element, {
            childList: true,
            subtree: true,
            characterData: true,
        });
        const parentElement = element.parentElement;
        if (parentElement) {
            const parentObserver = new MutationObserver(([mutation]) => {
                mutation.removedNodes.forEach((removedNode) => {
                    if (removedNode === element) {
                        this.dispatchPageEndEvent();
                    }
                });
            });
            parentObserver.observe(element.parentElement, { childList: true });
        }
    }
    getName(element) {
        if (typeof this.options.customGetName === "function") {
            return this.options.customGetName(element) ?? "";
        }
        if (element instanceof HTMLElement) {
            return element.innerText;
        }
        else {
            return "";
        }
    }
    findElement() {
        return document.querySelector(this.options.selector);
    }
}

class HashPageObserver extends PageObserver {
    constructor() {
        super(...arguments);
        this.log = Logger.create("HashPageObserver");
    }
    start() {
        let oldPage = this.getHash();
        this.dispatchPageChangeEvent(oldPage);
        window.addEventListener("hashchange", () => {
            const newPage = this.getHash();
            if (newPage && newPage !== oldPage) {
                oldPage = newPage;
                this.dispatchPageEndEvent();
                this.dispatchPageChangeEvent(newPage);
            }
        });
    }
    getHash() {
        return window.location.hash.slice(1);
    }
}

class ModalObserver {
    constructor(callback) {
        this.callback = callback;
        this.log = Logger.create("ModalObserver");
    }
    start() {
        const rootElement = this.getRootElement();
        if (!rootElement) {
            this.log.warn("Unable to find the root element for modals");
            return;
        }
        const rootElementObserver = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                mutation.removedNodes.forEach((wrapper) => {
                    if (wrapper instanceof HTMLElement &&
                        wrapper.classList.contains("modal-wrapper")) {
                        const id = this.getModalId(wrapper);
                        if (id) {
                            try {
                                this.callback({
                                    type: "close",
                                    id,
                                });
                            }
                            catch (err) {
                                this.log.error("Error in modal callback:", err);
                            }
                        }
                    }
                });
                mutation.addedNodes.forEach((wrapper) => {
                    if (wrapper instanceof HTMLElement &&
                        wrapper.classList.contains("modal-wrapper")) {
                        const id = this.getModalId(wrapper);
                        if (id) {
                            try {
                                this.callback({
                                    type: "open",
                                    id,
                                });
                            }
                            catch (err) {
                                this.log.error("Error in modal callback:", err);
                            }
                        }
                    }
                });
            }
        });
        rootElementObserver.observe(rootElement, {
            childList: true,
        });
    }
    stop() {
        // TODO: Implement to clear up observers etc
    }
    getRootElement() {
        return document.getElementById("ids-modal-root");
    }
    getModalId(modalWrapperElement) {
        return modalWrapperElement.querySelector(".modal")?.id ?? "";
    }
    isActive(modalWrapperElement) {
        return (modalWrapperElement
            .querySelector(".modal")
            ?.classList.contains("is-active") || false);
    }
}

class ParamHashPageObserver extends PageObserver {
    constructor() {
        super(...arguments);
        this.log = Logger.create("PathPageObserver");
    }
    start() {
        this.checkAllUrlChanges();
        const pushState = history.pushState;
        history.pushState = (state, unused, url) => {
            pushState.apply(history, [state, unused, url]);
            this.checkAllUrlChanges();
        };
        const replaceState = history.replaceState;
        history.replaceState = (data, unused, url) => {
            replaceState.apply(history, [data, unused, url]);
            this.checkAllUrlChanges();
        };
        window.addEventListener("popstate", () => this.checkAllUrlChanges());
    }
    checkAllUrlChanges() {
        try {
            const currentPath = location.pathname.slice(1);
            const urlSearch = location.search.slice(1);
            const urlHash = location.hash.slice(1);
            if (this.oldPath !== currentPath ||
                this.oldHash !== urlHash ||
                this.oldSearch !== urlSearch) {
                this.dispatchUrlChangeEvent(currentPath, urlHash, urlSearch);
                this.oldPath = currentPath;
                this.oldSearch = urlSearch;
                this.oldHash = urlHash;
            }
        }
        catch (error) {
            this.log.error("Error when checking location change:", error);
        }
    }
}

class PathPageObserver extends PageObserver {
    constructor() {
        super(...arguments);
        this.log = Logger.create("PathPageObserver");
    }
    start() {
        this.checkLocationChange();
        const pushState = history.pushState;
        history.pushState = (state, unused, url) => {
            pushState.apply(history, [state, unused, url]);
            this.checkLocationChange();
        };
        const replaceState = history.replaceState;
        history.replaceState = (data, unused, url) => {
            replaceState.apply(history, [data, unused, url]);
            this.checkLocationChange();
        };
        window.addEventListener("popstate", () => this.checkLocationChange());
    }
    checkLocationChange() {
        try {
            const currentPath = location.pathname.slice(1);
            if (currentPath !== this.oldPath) {
                this.dispatchPageEndEvent();
                this.dispatchPageChangeEvent(currentPath);
                this.oldPath = currentPath;
            }
        }
        catch (error) {
            this.log.error("Error when checking location change:", error);
        }
    }
}

class TitlePageObserver extends ElementPageObserver {
    constructor(options) {
        super({
            ignore: options?.ignore,
            selector: "head title",
        });
        this.log = Logger.create("TitlePageObserver");
    }
}

class EventObserver extends Observer {
    constructor() {
        super(...arguments);
        this.abortController = new AbortController();
        this.log = Logger.create("EventObserver");
    }
    observe(node, options) {
        node.addEventListener(options.type, (event) => {
            if (event.target instanceof HTMLElement) {
                let target = event.target;
                while (target && target.tagName !== "BODY") {
                    const eventName = this.getEventName(target, options);
                    if (eventName) {
                        if (this.isIgnored(eventName, options.ignore)) {
                            return;
                        }
                        this.triggerCallback({
                            type: options.type,
                            name: eventName,
                        });
                        return;
                    }
                    else {
                        if (!options.parents) {
                            return;
                        }
                        target = target.parentElement;
                    }
                }
            }
        }, {
            capture: true,
            passive: true,
            signal: this.abortController.signal,
        });
    }
    disconnect() {
        this.abortController.abort();
    }
    getEventName(element, options) {
        const attr = options.attributes.find((attribute) => element.hasAttribute(attribute));
        if (attr) {
            const value = element.getAttribute(attr);
            if (value) {
                return value;
            }
        }
    }
    isIgnored(eventName, ignoreList = []) {
        return ignoreList.some((ignore) => {
            if (typeof ignore === "string") {
                return eventName === ignore;
            }
            else {
                return eventName.match(ignore);
            }
        });
    }
}

class TrackingUtil {
    static timestamp() {
        return Date.now();
    }
}
function generateGuid() {
    return crypto.randomUUID();
}
/**
 * Uses the [Page Visibility API](https://developer.mozilla.org/en-US/docs/Web/API/Page_Visibility_API)
 * to track visibility of the browser tab.
 *
 * NOTE: This will keep track of each "frame" when the window is hidden. This list can
 * theoretically grow until it becomes a performance issue. TODO: Deal with it.
 */
class VisibilityObserver {
    constructor() {
        this.hiddenFrames = [];
        this.listenerOptions = {
            capture: true,
            passive: true,
        };
        this.handleVisibilityChange = () => {
            if (this.isHidden()) {
                this.hiddenFrames.push({ start: Date.now() });
            }
            else if (this.hiddenFrames.length) {
                const lastWindow = this.hiddenFrames[this.hiddenFrames.length - 1];
                lastWindow.end = Date.now();
            }
        };
    }
    /**
     * Start the observer, which will set up event listeners.
     */
    observe() {
        this.hiddenFrames = [];
        this.handleVisibilityChange();
        document.addEventListener("visibilitychange", this.handleVisibilityChange, this.listenerOptions);
    }
    /**
     * Stop the observer and free resources.
     */
    disconnect() {
        document.removeEventListener("visibilitychange", this.handleVisibilityChange, this.listenerOptions);
    }
    /**
     * Get the total time the document has been visible during a certain period.
     * @param start A timestamp
     * @param end A timestamp
     */
    visibleTime(start, end) {
        const frames = this.deepCopy(this.hiddenFrames);
        if (!frames.length) {
            return end - start;
        }
        const lastFrame = frames[frames.length - 1];
        if (!lastFrame.end || lastFrame.end > end) {
            // The window might currently be hidden, which means there is no "end" for the last frame
            lastFrame.end = end;
        }
        const firstFrame = frames[0];
        if (firstFrame.start < start) {
            firstFrame.start = start;
        }
        const relevantFrames = this.validFrames(frames).filter((f) => f.start <= end && f.end >= start);
        return relevantFrames.reduce((total, f) => total - (f.end - f.start), end - start);
    }
    deepCopy(obj) {
        return JSON.parse(JSON.stringify(obj));
    }
    isHidden() {
        return document.visibilityState === "hidden";
    }
    validFrames(frames) {
        return frames.filter((frame) => frame.start && frame.end);
    }
}
class AssertionError extends Error {
    constructor(message) {
        super(message);
        this.name = "AssertionError";
    }
}
class Assert {
    static defined(value, message) {
        if (value === null || value === undefined) {
            throw new AssertionError(message ?? `Expected value to be defined, got ${value}`);
        }
    }
    static definedProperty(object, property) {
        if (object[property] === null || object[property] === undefined) {
            throw new AssertionError(`Expected property '${String(property)}' to be defined, but received ${object[property]}`);
        }
    }
    static nonEmptyString(value, message) {
        message =
            message || `Expected value to be a non-empty string, got ${value}`;
        Assert.defined(value, message);
        if (typeof value !== "string") {
            throw new AssertionError(message);
        }
        if (!value.length) {
            throw new AssertionError(message);
        }
    }
    static true(condition, message) {
        message = message || `Expected condition to be true, got ${condition}`;
        if (!condition) {
            throw new AssertionError(message);
        }
    }
    static stringWithLength(value, opts, message) {
        Assert.nonEmptyString(value, message);
        Assert.true(value.length >= opts.min, message);
        Assert.true(value.length <= opts.max, message);
    }
    static positiveInteger(value, message) {
        message =
            message || `Expected value to be a positive integer, got ${value}`;
        Assert.defined(value, message);
        if (typeof value !== "number") {
            throw new AssertionError(message);
        }
        if (!Number.isInteger(value)) {
            throw new AssertionError(message);
        }
        if (value < 0) {
            throw new AssertionError(message);
        }
    }
    static match(value, pattern, message) {
        message = message || `Value must match pattern ${pattern}, got ${value}`;
        if (!pattern.test(value)) {
            throw new AssertionError(message);
        }
    }
    static isGuid(value, message) {
        message = message || `Expected value to be a GUID, got ${value}`;
        Assert.stringWithLength(value, { min: 36, max: 36 }, message);
        Assert.match(value, /^[A-Z0-9-]+$/i, message);
    }
}
class SDKConstants {
}
SDKConstants.YG_PG_TRACKER_APP = "yg_pg_tracker_app";
SDKConstants.YG_PG_TRACKER_VIEWS = "yg_pg_tracker_views";
SDKConstants.YG_PG_TRACKER_CURRENT_VIEW = "yg_pg_tracker_current_view";
SDKConstants.YG_TC_ACCESS_TOKEN = "yg_tc_access_token";
SDKConstants.YG_TC_LAST_SESSION = "yg_tc_last_session";
SDKConstants.YG_TC_TAB_GUID = "yg_tc_tab_guid";
SDKConstants.YG_DS_BASE_URL = "yg_ds_base_url";
SDKConstants.YG_PG_TRACKER_EVENTS = "yg_pg_base_events";
SDKConstants.YG_PG_TRACKER_CONTENTS = "yg_pg_base_contents";
SDKConstants.MAX_FAILED_BEFORE_STOP = 10;
SDKConstants.RETRY_STATUS_CODES = [503, 408];
SDKConstants.NON_RETRY_STATUS_CODES = [400, 401, 403];
SDKConstants.RETRY_AFTER = "retry-after";
SDKConstants.RETRY_ATTEMPTS = "retry-attempts";

class PageSessionStorage {
    setIsDevClient(devClient) {
        this.isDevClient = devClient;
    }
    async getApplication() {
        if (this.isDevClient)
            return null;
        return await JSON.parse(sessionStorage.getItem(SDKConstants.YG_PG_TRACKER_APP) || "null");
    }
    async getCurrentViews() {
        if (this.isDevClient)
            return [];
        return await JSON.parse(sessionStorage.getItem(SDKConstants.YG_PG_TRACKER_CURRENT_VIEW) || "[]");
    }
    async getOldViews() {
        if (this.isDevClient)
            return [];
        return await JSON.parse(sessionStorage.getItem(SDKConstants.YG_PG_TRACKER_VIEWS) || "[]");
    }
    async getEvents() {
        if (this.isDevClient)
            return [];
        return await JSON.parse(sessionStorage.getItem(SDKConstants.YG_PG_TRACKER_EVENTS) || "[]");
    }
    async getContents() {
        if (this.isDevClient)
            return [];
        return await JSON.parse(sessionStorage.getItem(SDKConstants.YG_PG_TRACKER_CONTENTS) || "[]");
    }
    async updateAppliaction(application) {
        if (this.isDevClient)
            return;
        await sessionStorage.setItem(SDKConstants.YG_PG_TRACKER_APP, JSON.stringify(application));
    }
    async updateCurrentViews(currentView) {
        if (this.isDevClient || !currentView)
            return;
        const existingViews = await this.getCurrentViews();
        const foundIndex = existingViews.findIndex((exView) => exView.guid === currentView.guid);
        if (foundIndex > -1) {
            existingViews.splice(foundIndex, 1, currentView);
        }
        else {
            existingViews.push(currentView);
        }
        await sessionStorage.setItem(SDKConstants.YG_PG_TRACKER_CURRENT_VIEW, JSON.stringify(existingViews));
    }
    async removeEndedCurrentItems() {
        if (this.isDevClient)
            return;
        const existingViews = await this.getCurrentViews();
        const existingEvents = await this.getEvents();
        const existingContents = await this.getContents();
        const endedView = existingViews.filter((view) => view.endTimeStamp);
        endedView.forEach((view) => {
            const viewIndex = existingViews.findIndex((curView) => curView.guid == view.guid);
            if (viewIndex > -1) {
                existingViews.splice(viewIndex, 1);
            }
            const eventIndex = existingEvents.findIndex((event) => event.viewGuid == view.guid);
            if (eventIndex > -1) {
                existingEvents.splice(eventIndex, 1);
            }
            const contentIndex = existingContents.findIndex((content) => content.viewGuid == view.guid);
            if (contentIndex > -1) {
                existingEvents.splice(contentIndex, 1);
            }
        });
        await sessionStorage.setItem(SDKConstants.YG_PG_TRACKER_CURRENT_VIEW, JSON.stringify(existingViews));
        await sessionStorage.setItem(SDKConstants.YG_PG_TRACKER_EVENTS, JSON.stringify(existingEvents));
        await sessionStorage.setItem(SDKConstants.YG_PG_TRACKER_CONTENTS, JSON.stringify(existingEvents));
    }
    async updateOldViews(views) {
        if (this.isDevClient || !views)
            return;
        await sessionStorage.setItem(SDKConstants.YG_PG_TRACKER_VIEWS, JSON.stringify(views));
    }
    async updateViewEvents(event) {
        if (this.isDevClient || !event)
            return;
        const events = [...(await this.getEvents()), event];
        await sessionStorage.setItem(SDKConstants.YG_PG_TRACKER_EVENTS, JSON.stringify(events));
    }
    async updateViewContents(content) {
        if (this.isDevClient || !content)
            return;
        const contents = [...(await this.getContents()), content];
        await sessionStorage.setItem(SDKConstants.YG_PG_TRACKER_CONTENTS, JSON.stringify(contents));
    }
    async clearCurrentViews() {
        if (this.isDevClient)
            return;
        sessionStorage.setItem(SDKConstants.YG_PG_TRACKER_CURRENT_VIEW, JSON.stringify([]));
    }
    async clearOldViews() {
        if (this.isDevClient)
            return;
        sessionStorage.setItem(SDKConstants.YG_PG_TRACKER_VIEWS, JSON.stringify([]));
    }
    async clearViewEvents() {
        if (this.isDevClient)
            return;
        sessionStorage.setItem(SDKConstants.YG_PG_TRACKER_EVENTS, JSON.stringify([]));
    }
    async clearViewContents() {
        if (this.isDevClient)
            return;
        sessionStorage.setItem(SDKConstants.YG_PG_TRACKER_CONTENTS, JSON.stringify([]));
    }
}

class BrowserCompatibility {
    constructor() {
        this.log = Logger.create(`Browser Compatibility Error`);
        this.isBrowserSupported = true;
        if (BrowserCompatibility._instance) {
            throw new Error("BrowserCompatibility Error: Instantiation failed: Use BrowserCompatibility.getInstance() instead of new.");
        }
        this.supported();
        BrowserCompatibility._instance = this;
    }
    supported() {
        try {
            /*
             * This is the highest JS feature used in the library
             * Update the same if a new feature is used in the feature
             */
            window.crypto.randomUUID();
        }
        catch (e) {
            this.log.error("The library uses `crypto.randomUUID()` & the current browser version is not compatible, please check https://developer.mozilla.org/en-US/docs/Web/API/Crypto/randomUUID for more details");
            this.isBrowserSupported = false;
        }
    }
    static getInstance() {
        return BrowserCompatibility._instance;
    }
    setConfig(config = {}) {
        this.config = config;
    }
    isSafeToProceed() {
        if (!this.isBrowserSupported) {
            // browser not supported
            return false;
        }
        else if (window.navigator.webdriver) {
            // navigator running on webdriver
            return this.config?.enableOnWebdriver || false; // if not set always disabled on webdriver
        }
        else {
            return this.config?.disableTracking ? false : true; // if not set tracking is always enabled
        }
    }
}
BrowserCompatibility._instance = new BrowserCompatibility();

const templatePrefix = "__temp_traker";
const expriyinDays = 5;
class TemplateManager {
    constructor(trackingClient) {
        this.trackingClient = trackingClient;
        this.log = Logger.create("TemplateManager");
        this.templateStorage = new TemplateStorage(templatePrefix);
    }
    async getTemplateByModleId(templateId) {
        if (!templateId) {
            return undefined;
        }
        const storageTemplate = this.templateStorage?.getTemplate(templateId);
        if (storageTemplate !== null) {
            return storageTemplate;
        }
        else {
            const templateModle = await this.trackingClient.loadTemplate({
                identifier: {
                    logicalId: templateId,
                },
            });
            const appModel = templateModle.template;
            this.templateStorage?.setTemplate(templateId, appModel.application);
            return appModel.application;
        }
    }
}
class TemplateStorage {
    constructor(prefix) {
        this.prefix = prefix;
        this.log = Logger.create("TemplateStorage");
    }
    getTemplate(templateId) {
        const sorage = localStorage.getItem(`${this.prefix}__${templateId}`);
        if (sorage) {
            const storeJson = JSON.parse(sorage);
            if (!this.checkExpiry(storeJson)) {
                return storeJson?.application;
            }
            else {
                return null;
            }
        }
        else {
            return null;
        }
    }
    checkExpiry(storeJson) {
        const now = new Date().getTime();
        const expiryDate = new Date(storeJson.expiryDate).getTime();
        return now > expiryDate;
    }
    clearTemplate(templateId) {
        localStorage.removeItem(`${this.prefix}__${templateId}`);
    }
    setTemplate(templateId, data) {
        const expiryDate = new Date().getTime() + expriyinDays * 60 * 1000;
        const localTemplate = {
            expiryDate: expiryDate,
            application: data,
        };
        localStorage.setItem(`${this.prefix}__${templateId}`, JSON.stringify(localTemplate));
    }
}
async function loadTemplatelocally(url) {
    const request = {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
        },
    };
    const bc = BrowserCompatibility.getInstance();
    if (!bc.isSafeToProceed()) {
        return null;
    }
    const response = await fetch(url, request);
    if (!response.ok) {
        console.error("Could not load the template");
    }
    const body = await response.json();
    const appModel = {};
    appModel.name = "localTemplate";
    appModel.guid = generateGuid();
    appModel.state = "ACTIVE";
    appModel.application = body;
    return { template: appModel };
}

/**
 * This is the tracking client for InforOS applications.
 * It will relay messages to InforOS Portal which will perform further processing.
 */
class ProxyTrackingClient {
    constructor(config) {
        this.config = config;
        this.browserSupported = BrowserCompatibility.getInstance();
        this.proxyConfig = {
            enableOnWebdriver: false,
            disableTracking: false,
            ...this.config,
        };
        this.browserSupported.setConfig(this.proxyConfig);
    }
    trackApplicationStart(app) {
        this.sendMessageToMaster({
            entity: app,
            entityType: "application.start",
        }, app.trackingStrategy, this.config.referanceId);
    }
    trackApplicationEnd(app) {
        this.sendMessageToMaster({
            entity: app,
            entityType: "application.end",
        }, app.trackingStrategy, this.config.referanceId);
    }
    trackApplicationEvent(event) {
        return;
    }
    trackViewStart(view) {
        this.sendMessageToMaster({
            entity: view,
            entityType: "view.start",
        }, undefined, this.config.referanceId);
    }
    trackViewEnd(view) {
        this.sendMessageToMaster({
            entity: view,
            entityType: "view.end",
        }, undefined, this.config.referanceId);
    }
    trackEvent(event) {
        this.sendMessageToMaster({
            entity: event,
            entityType: "event",
        }, undefined, this.config.referanceId);
    }
    trackContent(content) {
        this.sendMessageToMaster({
            entity: content,
            entityType: "content",
        }, undefined, this.config.referanceId);
    }
    loadTemplate(request) {
        const clientPromise = new DevTrackingClient();
        return clientPromise.loadTemplate(request);
    }
    setSessionStore(sessionStorage) {
        return;
    }
    getConfig() {
        return this.config;
    }
    sendMessageToMaster(content, strategy, referanceId) {
        const message = {
            type: "infor.portal.tracking.message",
            content,
            strategy,
            referanceId,
        };
        const targetWindow = this.config.targetWindow || window.top;
        const targetOrigin = this.config.targetWindowOrigin || "*";
        if (!targetWindow) {
            throw new Error(`Target window is not defined`);
        }
        targetWindow.postMessage(message, targetOrigin);
    }
}
const listenForProxyMessages = (context) => {
    function isProxyMessage(eventData = {}) {
        return (eventData.type ===
            "infor.portal.tracking.message");
    }
    const listener = ({ data }) => {
        if (isProxyMessage(data)) {
            const { content } = data;
            switch (content.entityType) {
                case "application.start":
                    context.trackApplicationStart(content.entity, data.referanceId);
                    break;
                case "application.end":
                    context.trackApplicationEnd(content.entity, data.referanceId);
                    break;
                case "view.start":
                    context.trackViewStart(content.entity, data.referanceId);
                    break;
                case "view.end":
                    context.trackViewEnd(content.entity, data.referanceId);
                    break;
                case "event":
                    context.trackEvent(content.entity, data.referanceId);
                    break;
                case "content":
                    context.trackContent(content.entity, data.referanceId);
                    break;
                default:
                    console.warn("Could not parse message content", content);
            }
        }
    };
    addEventListener("message", listener);
};

function validateApplication(application) {
    validateEntity(application);
    Assert.definedProperty(application, "guid");
    Assert.stringWithLength(application.identifier, { min: 1, max: 256 }, `Application identifier must be a string with 1 to 256 characters, got ${application.identifier}`);
    Assert.stringWithLength(application.hostname, { min: 1, max: 256 }, `Application hostname must be a string with 1 to 256 characters, got ${application.hostname}`);
    if (isDefined(application.version)) {
        Assert.stringWithLength(application.version, { min: 1, max: 256 }, `Application version must be a string with 1 to 256 characters if it is set, got ${application.version}`);
    }
}
function validateView(view) {
    validateEntity(view);
    Assert.definedProperty(view, "guid");
    Assert.definedProperty(view, "appGuid");
}
function validateContent(content) {
    validateEntity(content);
    Assert.definedProperty(content, "appGuid");
    Assert.definedProperty(content, "viewGuid");
}
function validateEvent(event) {
    validateEntity(event);
    Assert.definedProperty(event, "appGuid");
    Assert.definedProperty(event, "viewGuid");
}
function validateApplicationEvent(event) {
    validateEntity(event);
    Assert.definedProperty(event, "appGuid");
    Assert.stringWithLength(event.source, { min: 1, max: 256 }, `Application Event source must be a string with 1 to 256 characters, got ${event.source}`);
}
function validateEntity(entity) {
    validateName();
    validateType();
    validateTimestamp();
    validateAttributes();
    validateTags();
    validateFeatures();
    validateSiteWorkflows();
    validateGlobalWorkflows();
    function validateAttributes() {
        if (isDefined(entity.attributes)) {
            const numKeys = Object.keys(entity.attributes).length;
            Assert.true(numKeys <= 30, `There can be at most 30 attributes, got ${numKeys}`);
            for (const key in entity.attributes) {
                if (Object.prototype.hasOwnProperty.call(entity.attributes, key)) {
                    const value = entity.attributes[key];
                    if (isDefined(value)) {
                        Assert.stringWithLength(value.toString(), { min: 1, max: 2048 }, `Attribute values must be between 1 and 2048 characters, got ${value}`);
                    }
                    Assert.stringWithLength(key, { min: 1, max: 256 }, `Attribute key must be a string with 1 to 256 characters, got ${key}`);
                    const pattern = /^[A-z0-9-_]+$/g;
                    Assert.match(key, pattern, `Attribute key must match ${pattern}, got ${key}`);
                }
            }
        }
    }
    function validateTimestamp() {
        Assert.positiveInteger(entity.timestamp, `Timestamp must be a positive integer, got ${entity.timestamp}`);
    }
    function validateName() {
        Assert.stringWithLength(entity.name, { min: 1, max: 256 }, `Name must be a string with 1 to 256 characters, got ${entity.name}`);
    }
    function validateType() {
        if (isDefined(entity.type)) {
            Assert.stringWithLength(entity.type, { min: 1, max: 256 }, `Type must be a string with 1 to 256 characters if it is set, got ${entity.type}`);
        }
    }
    function validateTags() {
        if (isDefined(entity.tags)) {
            Assert.true(entity.tags.length <= 10, `There can be at most 10 tags, got ${entity.tags.length}`);
            for (const tag of entity.tags) {
                Assert.stringWithLength(tag, { min: 1, max: 16 }, `A tag must be a string with 1 to 16 characters, got ${tag}`);
            }
        }
    }
    function validateFeatures() {
        if (isDefined(entity.features)) {
            Assert.true(entity.features.length <= 10, `There can be at most 10 tags, got ${entity.features.length}`);
            for (const featureTag of entity.features) {
                Assert.stringWithLength(featureTag, { min: 1, max: 16 }, `A tag must be a string with 1 to 16 characters, got ${featureTag}`);
            }
        }
    }
    function validateSiteWorkflows() {
        if (isDefined(entity.siteWorkflows)) {
            Assert.true(entity.siteWorkflows.length <= 10, `There can be at most 10 tags, got ${entity.siteWorkflows.length}`);
            for (const siteWorkflowTag of entity.siteWorkflows) {
                Assert.stringWithLength(siteWorkflowTag, { min: 1, max: 16 }, `A tag must be a string with 1 to 16 characters, got ${siteWorkflowTag}`);
            }
        }
    }
    function validateGlobalWorkflows() {
        if (isDefined(entity.globalWorkflows)) {
            Assert.true(entity.globalWorkflows.length <= 10, `There can be at most 10 tags, got ${entity.globalWorkflows.length}`);
            for (const globalWorkflowTag of entity.globalWorkflows) {
                Assert.stringWithLength(globalWorkflowTag, { min: 1, max: 16 }, `A tag must be a string with 1 to 16 characters, got ${globalWorkflowTag}`);
            }
        }
    }
}
function isDefined(value) {
    return value !== null && value !== undefined;
}

/**
 * This client will only log messages, without sending anything.
 * Useful for development purposes.
 */
class DevTrackingClient {
    constructor(logginFn, configs) {
        this.configs = configs;
        this.appsMap = new Map();
        this.browserSupported = BrowserCompatibility.getInstance();
        this.devConfig = {
            enableOnWebdriver: false,
            disableTracking: false,
            ...this.configs,
        };
        this.browserSupported.setConfig(this.devConfig);
        if (!this.browserSupported.isSafeToProceed()) {
            return;
        }
        if (logginFn) {
            this.loggingFn = logginFn;
        }
        else {
            const log = Logger.create("DevTrackingClient");
            this.loggingFn = (...parts) => log.info(...parts);
        }
        this.log("Running in development mode. Events will be logged, but no data is sent.");
        if (window === window.top) {
            listenForProxyMessages(this);
        }
    }
    trackApplicationStart(app, proxyClientRefId) {
        if (proxyClientRefId) {
            return;
        }
        validateApplication(app);
        const parentGuid = this.getParentGuidByProxyClientRef(proxyClientRefId);
        const newApp = { ...app, parentGuid };
        this.log(`Application Start: ${app.name} track by ${app.trackingStrategy} strategy`, app);
        this.appsMap.set(app.guid, {
            application: app,
            views: new Map(),
        });
        this.sessionStore?.removeEndedCurrentItems();
    }
    trackApplicationEnd(app, proxyClientRefId) {
        if (proxyClientRefId) {
            return;
        }
        validateApplication(app);
        const parentGuid = this.getParentGuidByProxyClientRef(proxyClientRefId);
        const newApp = { ...app, parentGuid };
        this.log(`Application End: ${app.name} track by ${app.trackingStrategy} strategy`, app);
        this.appsMap.delete(app.guid);
    }
    trackApplicationEvent(event, proxyClientRefId) {
        if (proxyClientRefId) {
            return;
        }
        validateApplicationEvent(event);
        const app = this.appsMap.get(event.appGuid);
        this.log(`Application Event: ${event.name} track by ${app?.application.trackingStrategy} strategy`, event);
    }
    trackViewStart(view, proxyClientRefId) {
        validateView(view);
        let parentView;
        let app = this.appsMap.get(view.appGuid);
        if (proxyClientRefId) {
            parentView = this.getParentGuidByProxyClientRef(proxyClientRefId);
            if (parentView?.appKey) {
                app = this.appsMap.get(parentView?.appKey);
            }
        }
        view = { ...view, parentGuid: parentView?.view.guid };
        app?.views.set(view.guid, view);
        this.log(`View Start: ${app?.application.name}/${view.name} track by ${app?.application.trackingStrategy} strategy`, view);
    }
    trackViewEnd(view, proxyClientRefId) {
        validateView(view);
        let app = this.appsMap.get(view.appGuid);
        let parentView;
        if (proxyClientRefId) {
            parentView = this.getParentGuidByProxyClientRef(proxyClientRefId);
            if (parentView?.appKey) {
                app = this.appsMap.get(parentView?.appKey);
            }
        }
        view = { ...view, parentGuid: parentView?.view.guid };
        if (app && view.proxyClientRef) {
            this.updateStatusOfChildViews(app.application.guid, view.proxyClientRef, view.guid);
        }
        this.log(`View End: ${app?.application.name}/${view.name} track by ${app?.application.trackingStrategy} strategy`, view);
        app?.views.delete(view.guid);
    }
    trackEvent(event, proxyClientRefId) {
        validateEvent(event);
        let app = this.appsMap.get(event.appGuid);
        let parentView;
        if (proxyClientRefId) {
            parentView = this.getParentGuidByProxyClientRef(proxyClientRefId);
            if (parentView?.appKey) {
                app = this.appsMap.get(parentView?.appKey);
            }
        }
        const view = app?.views.get(event.viewGuid);
        const newEvent = { ...event, parentGuid: parentView?.view.guid };
        this.log(`Event: ${app?.application.name}/${view?.name}/${event.name} (${event.type}) track by ${app?.application.trackingStrategy} strategy`, newEvent);
    }
    trackContent(content, proxyClientRefId) {
        validateContent(content);
        let app = this.appsMap.get(content.appGuid);
        let parentView;
        if (proxyClientRefId) {
            parentView = this.getParentGuidByProxyClientRef(proxyClientRefId);
            if (parentView?.appKey) {
                app = this.appsMap.get(parentView?.appKey);
            }
        }
        const view = app?.views.get(content.viewGuid);
        const newContent = { ...content, parentGuid: parentView?.view.guid };
        this.log(`Content: ${app?.application.name}/${view?.name}/${content.name} (${content.type}) track by ${app?.application.trackingStrategy} strategy`, newContent);
    }
    setSessionStore(sessionStorage) {
        this.sessionStore = sessionStorage;
    }
    getConfig() {
        return this.devConfig;
    }
    async loadTemplate(request) {
        // For Dev client testing when web security disabled or template host is same
        const templateHost = localStorage.getItem(`${templatePrefix}__dev__host`) || "/assets/";
        return await loadTemplatelocally(`${templateHost}/${request.identifier.logicalId || request.identifier.hostname}.json`);
    }
    log(...parts) {
        this.loggingFn && this.loggingFn("[DevTrackingClient]", ...parts);
    }
    getParentGuidByProxyClientRef(proxyClientRef) {
        if (!proxyClientRef) {
            return;
        }
        for (const [key, entry] of this.appsMap.entries()) {
            const views = entry.views.size ? [...entry.views.values()] : undefined;
            const foundView = views?.find((view) => view.proxyClientRef === proxyClientRef);
            if (foundView) {
                return {
                    appKey: key,
                    view: foundView,
                };
            }
        }
        return;
    }
    updateStatusOfChildViews(appKey, proxyClientRef, guid) {
        if (proxyClientRef && guid) {
            const app = this.appsMap.get(appKey);
            if (app) {
                const appViews = [...app.views.values()];
                appViews
                    .filter((x) => x.parentGuid === guid)
                    .forEach((runingChildview) => {
                    if (runingChildview.proxyClientRef) {
                        this.updateStatusOfChildViews(appKey, runingChildview.proxyClientRef, runingChildview.guid);
                    }
                    // child views bounce status
                    const childBounceState = appViews
                        .filter((x) => x.parentGuid === guid)
                        .find((y) => !y.bounce)
                        ? false
                        : true;
                    runingChildview.bounce = runingChildview.bounce && childBounceState;
                    this.log(`View End: ${app?.application.name}/${runingChildview.name} track by ${app?.application.trackingStrategy} strategy`, runingChildview);
                    this.appsMap.get(appKey)?.views.delete(runingChildview.guid);
                });
            }
        }
    }
}

/**
 * The EventTarget constructor is not supported in all browsers, so this implementation
 * with a dummy element is used instead.
 *
 * https://caniuse.com/mdn-api_eventtarget_eventtarget
 */
class EventEmitter {
    constructor() {
        this.dummy = document.createDocumentFragment();
    }
    addEventListener(type, listener, options) {
        return this.dummy.addEventListener(type, listener, options);
    }
    dispatchEvent(event) {
        return this.dummy.dispatchEvent(event);
    }
    removeEventListener(type, callback, options) {
        return this.dummy.removeEventListener(type, callback, options);
    }
}
class StatefulTracker extends EventEmitter {
    constructor() {
        super(...arguments);
        this.started = false;
    }
    waitUntilStarted() {
        return new Promise((resolve) => {
            if (this.started) {
                resolve();
            }
            else {
                this.addEventListener("start", () => resolve(), { once: true });
            }
        });
    }
    addEventListener(type, listener, options) {
        return super.addEventListener(type, listener, options);
    }
    removeEventListener(type, callback, options) {
        return super.removeEventListener(type, callback, options);
    }
    onbeforestart() {
        this.log.debug("onbeforestart");
        this.dispatchEvent(new CustomEvent("beforestart"));
    }
    onstart() {
        this.log.debug("onstart");
        this.started = true;
        this.dispatchEvent(new CustomEvent("start"));
    }
    onbeforeend() {
        this.log.debug("onbeforeend");
        this.dispatchEvent(new CustomEvent("beforeend"));
    }
    onend() {
        this.log.debug("onend");
        this.started = false;
        this.dispatchEvent(new CustomEvent("end"));
    }
}
class ViewTracker extends StatefulTracker {
    constructor(opts) {
        super();
        this.children = [];
        this.browserSupported = BrowserCompatibility.getInstance();
        this.browserSupported.setConfig(opts.trackingClient.getConfig());
        this.name = opts.name;
        this.url = opts.url;
        this.application = opts.application;
        this.trackingClient = opts.trackingClient;
        this.attributes = opts.attributes;
        this.type = opts.type;
        this.tags = opts.tags;
        this.log = Logger.create(`ViewTracker (${this.name})`);
        this.features = opts.features;
        this.siteWorkflows = opts.siteWorkflows;
        this.globalWorkflows = opts.globalWorkflows;
        this.parent = opts.parent;
        this.proxyClientRef = opts.proxyClientRef;
    }
    async trackStart() {
        try {
            if (!this.browserSupported.isSafeToProceed()) {
                return;
            }
            if (this.parent) {
                await this.parent.waitUntilStarted();
            }
            else {
                await this.application.waitUntilStarted();
            }
            const appGuid = this.application.getAppGuid();
            Assert.nonEmptyString(appGuid, "appGuid is not set");
            this.viewInstance = {
                appGuid: appGuid,
                timestamp: TrackingUtil.timestamp(),
                guid: generateGuid(),
                parentGuid: this.parent?.viewInstance?.guid,
                name: this.name,
                url: this.url,
                attributes: this.attributes,
                type: this.type,
                tags: this.tags,
                features: this.features,
                siteWorkflows: this.siteWorkflows,
                globalWorkflows: this.globalWorkflows,
                proxyClientRef: this.proxyClientRef,
            };
            this.onbeforestart();
            this.trackingClient.trackViewStart(this.viewInstance);
            this.onstart();
        }
        catch (error) {
            this.log.error("trackStart failed:", error);
        }
    }
    getTrackedView() {
        if (!this.browserSupported.isSafeToProceed()) {
            return;
        }
        return this.viewInstance;
    }
    async trackEnd() {
        try {
            if (!this.browserSupported.isSafeToProceed()) {
                return;
            }
            await this.application.waitUntilStarted();
            for (const child of this.children.filter((child) => child.started)) {
                await child.trackEnd();
            }
            Assert.defined(this.viewInstance);
            this.onbeforeend();
            this.trackingClient.trackViewEnd({
                ...this.viewInstance,
                timestamp: TrackingUtil.timestamp(),
            });
            this.onend();
        }
        catch (error) {
            this.log.error("trackEnd failed:", error);
        }
    }
    async trackEvent(name, type, attributes, tags, features, siteWorkflows, globalWorkflows, bounce) {
        try {
            if (!this.browserSupported.isSafeToProceed()) {
                return;
            }
            await this.waitUntilStarted();
            const appGuid = this.application.getAppGuid();
            const viewGuid = this.viewInstance?.guid;
            const eventGuid = generateGuid();
            Assert.nonEmptyString(appGuid, "appGuid is not set");
            Assert.nonEmptyString(viewGuid, "viewGuid is not set");
            Assert.nonEmptyString(eventGuid, "eventGuid is not set");
            const trackedEvent = {
                name,
                timestamp: TrackingUtil.timestamp(),
                appGuid,
                viewGuid,
                type,
                attributes,
                tags,
                guid: eventGuid,
                features,
                siteWorkflows,
                globalWorkflows,
                bounce,
            };
            this.trackingClient.trackEvent(trackedEvent);
            return trackedEvent;
        }
        catch (error) {
            this.log.error("trackEvent failed:", error);
        }
    }
    async trackContent(name, type, attributes, tags, features, siteWorkflows, globalWorkflows) {
        try {
            if (!this.browserSupported.isSafeToProceed()) {
                return;
            }
            await this.waitUntilStarted();
            const appGuid = this.application.getAppGuid();
            const viewGuid = this.viewInstance?.guid;
            const contentGuid = generateGuid();
            Assert.nonEmptyString(appGuid, "appGuid is not set");
            Assert.nonEmptyString(viewGuid, "viewGuid is not set");
            Assert.nonEmptyString(contentGuid, "contentGuid is not set");
            const contentInstance = {
                name,
                timestamp: TrackingUtil.timestamp(),
                appGuid,
                viewGuid,
                type,
                attributes,
                tags,
                guid: contentGuid,
                features,
                siteWorkflows,
                globalWorkflows,
            };
            this.trackingClient.trackContent(contentInstance);
            return contentInstance;
        }
        catch (error) {
            this.log.error("trackContent failed:", error);
        }
    }
    createChild(options) {
        const child = this.application.createView({
            parent: this,
            ...options,
        });
        this.children.push(child);
        return child;
    }
    getApplication() {
        return this.application;
    }
}
class PageViewTracker extends ViewTracker {
    constructor(opts) {
        super(opts);
        this.pageTrackingClient = opts.trackingClient;
        this.sessionStore = opts.sessionStore;
        this.init();
    }
    async init() {
        await this.trackStart();
    }
    async trackStart() {
        await super.trackStart();
        const trackInstance = super.getTrackedView();
        if (trackInstance) {
            trackInstance.startTimeStamp = trackInstance.timestamp;
            await this.sessionStore.updateCurrentViews(trackInstance);
        }
    }
    async trackEnd() {
        await super.trackEnd();
        const trackInstance = super.getTrackedView();
        if (trackInstance) {
            trackInstance.endTimeStamp = TrackingUtil.timestamp();
            if (trackInstance.parentGuid) {
                await this.sessionStore.updateCurrentViews(trackInstance);
            }
        }
    }
    async trackEvent(name, type, attributes, tags, features, siteWorkflows, globalWorkflows, bounce) {
        try {
            const trackedView = super.getTrackedView();
            if (trackedView) {
                const appGuid = super.getApplication().getAppGuid();
                const viewGuid = trackedView?.guid;
                if (appGuid && viewGuid) {
                    const eventInstance = await super.trackEvent(name, type, attributes, tags, features, siteWorkflows, globalWorkflows, bounce);
                    await this.sessionStore.updateViewEvents(eventInstance);
                }
            }
        }
        catch (error) {
            this.log.error("trackEvent failed:", error);
        }
    }
    async trackContent(name, type, attributes, tags, features, siteWorkflows, globalWorkflows) {
        try {
            const trackedView = super.getTrackedView();
            if (trackedView) {
                const appGuid = super.getApplication().getAppGuid();
                const viewGuid = trackedView?.guid;
                if (appGuid && viewGuid) {
                    const contentInstance = await super.trackContent(name, type, attributes, tags, features, siteWorkflows, globalWorkflows);
                    await this.sessionStore.updateViewContents(contentInstance);
                }
            }
        }
        catch (error) {
            this.log.error("trackEvent failed:", error);
        }
    }
    async pageChild(options) {
        const child = new PageViewTracker({
            application: this.getApplication(),
            parent: this,
            trackingClient: this.pageTrackingClient,
            sessionStore: this.sessionStore,
            ...options,
        });
        return child;
    }
}
class ApplicationTracker extends StatefulTracker {
    constructor(opts) {
        super();
        this.startegy = "CUSTOM";
        this.browserSupported = BrowserCompatibility.getInstance();
        this.children = [];
        this.browserSupported.setConfig(opts.trackingClient.getConfig());
        this.name = opts.name;
        this.identifier = opts.identifier;
        this.hostname = opts.hostname || location.hostname;
        this.trackingClient = opts.trackingClient;
        this.attributes = opts.attributes;
        this.type = opts.type;
        this.tags = opts.tags;
        this.version = opts.version;
        this.log = Logger.create(`ApplicationTracker (${this.name})`);
        this.features = opts.features;
        this.siteWorkflows = opts.siteWorkflows;
        this.globalWorkflows = opts.globalWorkflows;
        this.parent = opts.parent;
    }
    async trackStart() {
        try {
            if (!this.browserSupported.isSafeToProceed()) {
                return;
            }
            if (this.parent) {
                await this.parent.waitUntilStarted();
            }
            this.appInstance = {
                name: this.name,
                hostname: this.hostname,
                identifier: this.identifier,
                timestamp: TrackingUtil.timestamp(),
                guid: generateGuid(),
                parentGuid: this.parent?.appInstance?.guid,
                attributes: this.attributes,
                type: this.type,
                tags: this.tags,
                version: this.version,
                features: this.features,
                siteWorkflows: this.siteWorkflows,
                globalWorkflows: this.globalWorkflows,
                trackingStrategy: this.startegy,
            };
            this.onbeforestart();
            this.trackingClient.trackApplicationStart(this.appInstance);
            this.onstart();
        }
        catch (error) {
            this.log.error("trackStart failed:", error);
        }
    }
    getTrackedApplication() {
        return this.appInstance || null;
    }
    async trackEnd() {
        try {
            if (!this.browserSupported.isSafeToProceed()) {
                return;
            }
            await this.waitUntilStarted();
            for (const child of this.children.filter((child) => child.started)) {
                await child.trackEnd();
            }
            Assert.defined(this.appInstance);
            this.onbeforeend();
            this.trackingClient.trackApplicationEnd({
                ...this.appInstance,
                timestamp: TrackingUtil.timestamp(),
            });
            this.onend();
        }
        catch (error) {
            this.log.error("trackEnd failed:", error);
        }
    }
    async trackApplicationEvent(name, source, type, attributes, tags, features, siteWorkflows, globalWorkflows) {
        try {
            if (!this.browserSupported.isSafeToProceed()) {
                return;
            }
            await this.waitUntilStarted();
            const appGuid = this.appInstance?.guid;
            const guid = generateGuid();
            Assert.nonEmptyString(appGuid, "appGuid is not set");
            Assert.nonEmptyString(guid, "app event guid is not set");
            this.trackingClient.trackApplicationEvent({
                timestamp: TrackingUtil.timestamp(),
                appGuid,
                name,
                source,
                type,
                attributes,
                tags,
                guid,
                features,
                siteWorkflows,
                globalWorkflows,
            });
        }
        catch (error) {
            this.log.error("trackApplicationEvent failed:", error);
        }
    }
    createChild(options) {
        let child;
        switch (this.startegy) {
            case "AUTO":
                child = new AutoApplicationTracker({
                    trackingClient: this.trackingClient,
                    parent: this,
                    ...options,
                });
                break;
            case "TEMPLATE":
                child = new TemplateApplicationTracker({
                    trackingClient: this.trackingClient,
                    parent: this,
                    ...options,
                });
                break;
            case "PLATFORM":
                child = new PlatformApplicationTracker({
                    trackingClient: this.trackingClient,
                    parent: this,
                    ...options,
                });
                break;
            default:
                child = new ApplicationTracker({
                    trackingClient: this.trackingClient,
                    parent: this,
                    ...options,
                });
        }
        this.children.push(child);
        return child;
    }
    getAppGuid() {
        return this.appInstance?.guid;
    }
    createView(opts) {
        return new ViewTracker({
            application: this,
            trackingClient: this.trackingClient,
            ...opts,
        });
    }
}
class AutoApplicationTracker extends ApplicationTracker {
    constructor(opts) {
        super(opts);
        this.startegy = "AUTO";
    }
}
class TemplateApplicationTracker extends ApplicationTracker {
    constructor(opts) {
        super(opts);
        this.startegy = "TEMPLATE";
    }
}
class PlatformApplicationTracker extends ApplicationTracker {
    constructor(opts) {
        super(opts);
        this.startegy = "PLATFORM";
    }
}
class NonSinglePageApplicationTracker extends ApplicationTracker {
    constructor(opts) {
        super(opts);
        this.opts = opts;
        this.startegy = "CUSTOM";
        this.sessionStore = new PageSessionStorage();
        this.appTrackingClient = opts.trackingClient;
        this.sessionStore.setIsDevClient(this.appTrackingClient instanceof DevTrackingClient);
        this.appTrackingClient.setSessionStore(this.sessionStore);
    }
    async trackStart() {
        try {
            let currentApp = await this.sessionStore.getApplication();
            if (currentApp &&
                currentApp.name === this.opts.name &&
                currentApp.identifier === this.opts.identifier &&
                currentApp.hostname === this.opts.hostname) {
                this.onbeforestart();
                this.appTrackingClient?.trackApplicationStart(currentApp);
                this.onstart();
            }
            else {
                super.trackStart();
                currentApp = super.getTrackedApplication();
                this.sessionStore.updateAppliaction(currentApp);
            }
            if (currentApp) {
                this.applicationInstance = currentApp;
            }
            await this.sendSessionTrackedViews();
            await this.sendSessionTrackedEvents();
            await this.sendSessionTrackedContents();
        }
        catch (error) {
            this.log.error("trackStart failed:", error);
        }
    }
    getTrackedApplication() {
        return this.applicationInstance || null;
    }
    createView(opts) {
        return new PageViewTracker({
            application: this,
            trackingClient: this.appTrackingClient,
            sessionStore: this.sessionStore,
            ...opts,
        });
    }
    getAppGuid() {
        return super.getAppGuid() || this.applicationInstance?.guid;
    }
    getLastBounceEvent(view, events) {
        const eventArr = events
            .filter((evt) => evt.bounce == true)
            .sort((a, b) => a.timestamp - b.timestamp)
            .map((x) => x.timestamp);
        return eventArr.length > 0
            ? Math.max(...eventArr)
            : TrackingUtil.timestamp();
    }
    async sendSessionTrackedViews() {
        let currentViews = await this.sessionStore.getCurrentViews();
        let sessionViews = await this.sessionStore.getOldViews();
        const sessionEvents = await this.sessionStore.getEvents();
        currentViews = currentViews.map((curView) => {
            const notBounceEvent = sessionEvents.filter((trackedEvent) => trackedEvent.viewGuid === curView.guid && !trackedEvent.bounce);
            const lastEventTimestamp = this.getLastBounceEvent(curView, sessionEvents);
            return {
                ...curView,
                bounce: notBounceEvent.length > 0 ? false : true,
                endTimeStamp: lastEventTimestamp,
            };
        });
        sessionViews = [...sessionViews, ...currentViews];
        await this.sessionStore.updateOldViews(sessionViews);
        await this.sessionStore.clearCurrentViews();
        const currentApp = await this.sessionStore.getApplication();
        sessionViews.forEach((view) => {
            if (currentApp && currentApp.guid === view.appGuid) {
                const { startTimeStamp, endTimeStamp } = view;
                view.timestamp = startTimeStamp || view.timestamp;
                delete view.startTimeStamp;
                delete view.endTimeStamp;
                this.appTrackingClient?.trackViewStart(view);
                if (endTimeStamp) {
                    view.timestamp = endTimeStamp || TrackingUtil.timestamp();
                    this.appTrackingClient?.trackViewEnd(view);
                }
            }
        });
    }
    async sendSessionTrackedEvents() {
        const sessionEvents = await this.sessionStore.getEvents();
        const currentApp = await this.sessionStore.getApplication();
        sessionEvents.forEach((event) => {
            if (currentApp && currentApp.guid == event.appGuid) {
                this.appTrackingClient?.trackEvent(event);
            }
        });
    }
    async sendSessionTrackedContents() {
        const sessionContents = await this.sessionStore.getContents();
        const currentApp = await this.sessionStore.getApplication();
        sessionContents.forEach((content) => {
            if (currentApp && currentApp.guid == content.appGuid) {
                this.appTrackingClient?.trackContent(content);
            }
        });
    }
}

function startAutoTracking(options) {
    const tracker = new AutoTracker(options);
    tracker.start();
}
class AutoTracker {
    get currentView() {
        if (this.currentModal) {
            return this.currentModal;
        }
        if (this.currentPage) {
            return this.currentPage;
        }
    }
    constructor(options) {
        this.options = options;
        this.log = Logger.create("AutoTracker");
        this.currentApp = new AutoApplicationTracker({
            name: options.application.name,
            identifier: options.application.identifier,
            trackingClient: options.trackingClient ??
                new DevTrackingClient(undefined, { enableOnWebdriver: true }),
        });
    }
    start() {
        this.log.debug("start");
        this.currentApp.trackStart();
        this.startPageTracking();
        this.startClickTracking();
        this.startModalTracking();
    }
    startPageTracking() {
        const observer = this.createPageObserver();
        observer.addEventListener("pageend", () => {
            this.currentPage?.trackEnd();
            this.currentPage = undefined;
        });
        observer.addEventListener("pagechange", (event) => {
            const name = event.detail.page;
            if (name.length) {
                this.log.debug(`pagechange "${name}"`);
                this.currentPage?.trackEnd();
                this.currentPage = this.currentApp.createView({
                    name: name,
                    type: "page",
                });
                this.currentPage.trackStart();
            }
        });
        observer.start();
    }
    startClickTracking() {
        const strategy = this.options.clicks?.track;
        if (!strategy) {
            this.log.debug("Click tracking strategy not configured. Tracking id:s by default.");
        }
        const observer = new EventObserver((record) => {
            this.currentView?.trackEvent(record.name, record.type);
        });
        observer.observe(document, {
            type: "click",
            attributes: strategy?.attributes ?? ["id"],
            ignore: strategy?.ignore,
            parents: strategy?.parents,
        });
    }
    startModalTracking() {
        const stack = [];
        const modalObserver = new ModalObserver(({ type, id }) => {
            if (type === "open") {
                this.log.debug(`Modal opened: ${id}`);
                if (this.currentModal) {
                    stack.push(this.currentModal);
                }
                this.currentModal = this.currentApp.createView({
                    name: id,
                    type: "modal",
                });
                this.currentModal.trackStart();
            }
            if (type === "close") {
                this.log.debug(`Modal closed: ${id}`);
                this.currentModal?.trackEnd();
                this.currentModal = stack.pop();
            }
        });
        modalObserver.start();
    }
    createPageObserver() {
        const strategy = this.options.pages?.track;
        const ignore = this.options.pages?.track.ignore;
        if (!strategy) {
            this.log.debug("Page tracking not configured. Using Path tracking by default");
            return new PathPageObserver();
        }
        switch (strategy.type) {
            case "hash":
                return new HashPageObserver({ ignore });
            case "path":
                return new PathPageObserver({ ignore });
            case "title":
                return new TitlePageObserver({ ignore });
            case "companyon":
                return new CompanyonPageObserver({
                    ignore,
                    messageType: strategy.message,
                    messageProperty: strategy.property,
                });
            case "element":
                return new ElementPageObserver({
                    ignore,
                    selector: strategy.selector,
                    customGetName: strategy.customName,
                });
            default:
                throw new TypeError(`Invalid view type: ${strategy}`);
        }
    }
}

/**
 * Helper function to make an XPathResult iterable, e.g for use in for ... of
 */
function* iterable(result) {
    switch (result.resultType) {
        case XPathResult.FIRST_ORDERED_NODE_TYPE:
        case XPathResult.ANY_UNORDERED_NODE_TYPE:
            if (result.singleNodeValue) {
                yield result.singleNodeValue;
            }
            break;
        case XPathResult.UNORDERED_NODE_ITERATOR_TYPE:
        case XPathResult.ORDERED_NODE_ITERATOR_TYPE: {
            let node = result.iterateNext();
            while (node) {
                yield node;
                node = result.iterateNext();
            }
            break;
        }
    }
}
function first(result) {
    switch (result.resultType) {
        case XPathResult.FIRST_ORDERED_NODE_TYPE:
        case XPathResult.ANY_UNORDERED_NODE_TYPE:
            return result.singleNodeValue;
        case XPathResult.UNORDERED_NODE_ITERATOR_TYPE:
        case XPathResult.ORDERED_NODE_ITERATOR_TYPE:
            return result.iterateNext();
        case XPathResult.STRING_TYPE:
            return result.stringValue;
        case XPathResult.NUMBER_TYPE:
            return result.numberValue;
        case XPathResult.BOOLEAN_TYPE:
            return result.booleanValue;
        default:
            return null;
    }
}
function query(expression, node = document, resultType) {
    const result = document.evaluate(expression, node, null, resultType);
    return result;
}
var ResultType;
(function (ResultType) {
    ResultType[ResultType["FIRST_ORDERED_NODE_TYPE"] = XPathResult.FIRST_ORDERED_NODE_TYPE] = "FIRST_ORDERED_NODE_TYPE";
    ResultType[ResultType["ANY_UNORDERED_NODE_TYPE"] = XPathResult.ANY_UNORDERED_NODE_TYPE] = "ANY_UNORDERED_NODE_TYPE";
    ResultType[ResultType["UNORDERED_NODE_ITERATOR_TYPE"] = XPathResult.UNORDERED_NODE_ITERATOR_TYPE] = "UNORDERED_NODE_ITERATOR_TYPE";
    ResultType[ResultType["ORDERED_NODE_ITERATOR_TYPE"] = XPathResult.ORDERED_NODE_ITERATOR_TYPE] = "ORDERED_NODE_ITERATOR_TYPE";
    ResultType[ResultType["STRING_TYPE"] = XPathResult.STRING_TYPE] = "STRING_TYPE";
    ResultType[ResultType["NUMBER_TYPE"] = XPathResult.NUMBER_TYPE] = "NUMBER_TYPE";
    ResultType[ResultType["BOOLEAN_TYPE"] = XPathResult.BOOLEAN_TYPE] = "BOOLEAN_TYPE";
})(ResultType || (ResultType = {}));

function startTemplateTracking(options) {
    const tracker = new TemplateTracker(options);
    tracker.setTrackerClient(options.trackingClient);
}
class TemplateTracker {
    constructor(options) {
        this.log = Logger.create("TemplateTracker");
        this.viewEvents = [];
        this.trackerMap = new Map();
        this.trackingClient = new DevTrackingClient();
        this.configuredApp = this.formatAppConfig(options.application);
    }
    setTrackerClient(client) {
        if (client) {
            this.trackingClient = client;
            this.templateManager = new TemplateManager(client);
        }
        else {
            this.log.error("For Template Tracking it must provide TrackingClient");
        }
        this.startTracking();
    }
    formatAppConfig(application) {
        return {
            id: application.id,
            appIdentifier: this.resolveAppIdentifier(application.appIdentifier),
            pageStrategyExp: application.pageStrategyExp,
        };
    }
    resolveAppIdentifier(appIdentifier) {
        const appIdEntries = Object.entries(appIdentifier || {});
        return appIdEntries.map((appEntry) => {
            const subEntries = Object.entries(appEntry[1]);
            return {
                type: appEntry[0],
                operator: subEntries[0][0],
                value: subEntries[0][1],
            };
        });
    }
    start() {
        this.log.debug("started to track", this.appModel);
        if (this.appModel?.name.static) {
            // TODO complete the otehr attributes of a application
            this.application = new TemplateApplicationTracker({
                name: this.appModel.name.static,
                identifier: this.appModel.name.static,
                trackingClient: this.trackingClient,
            });
            this.application.trackStart();
        }
    }
    async createViewsForDOMChanges() {
        const docReady = await this.isDocumentReady();
        if (docReady) {
            this.findViewsByXpath();
        }
    }
    clearAllViewTrackers() {
        this.trackerMap.forEach((tracker) => {
            tracker.trackEnd();
        });
        this.trackerMap.clear();
    }
    findViewsByXpath() {
        if (this.appModel) {
            const { views } = this.appModel;
            views
                .filter((view) => view.xpath)
                .forEach(async (view) => {
                const xpath = view.xpath || "";
                const viewFound = first(query(xpath));
                const viewKey = `${view?.name?.static}_${xpath}`;
                if (viewFound) {
                    const elementInView = await this.isElementIntersect(viewFound);
                    if (elementInView) {
                        // TODO complete the otehr attributes of a view
                        const viewTracker = this.application?.createView({
                            name: view?.name?.static || "NewView",
                            type: "view",
                        });
                        if (viewTracker) {
                            if (!this.trackerMap.get(viewKey)) {
                                this.trackerMap.set(viewKey, viewTracker);
                                viewTracker?.trackStart();
                            }
                            this.currentPageModel = view;
                            view.events?.forEach((event) => (event.pageKey = viewKey));
                            this.viewEvents = [
                                ...this.viewEvents,
                                ...view.events,
                            ];
                        }
                    }
                    else {
                        const foundTracker = this.trackerMap.get(viewKey);
                        if (foundTracker) {
                            foundTracker.trackEnd();
                            this.trackerMap.delete(viewKey);
                        }
                    }
                }
                else {
                    const foundTracker = this.trackerMap.get(viewKey);
                    if (foundTracker) {
                        foundTracker.trackEnd();
                        this.trackerMap.delete(viewKey);
                    }
                }
            });
        }
    }
    getEventDetails(model, element) {
        const attributes = {};
        for (const attributeModel of model.attributes ?? []) {
            const attributeValue = this.getText(attributeModel.value, element);
            if (attributeValue) {
                attributes[attributeModel.key] = attributeValue;
            }
        }
        const name = this.getText(model.name, element);
        return {
            name,
            type: model.type,
            attributes,
        };
    }
    getText(locator, node) {
        if (locator.static) {
            return locator.static;
        }
        if (locator.xpath) {
            const result = query(locator.xpath, node);
            const firstResult = first(result);
            if (firstResult !== null) {
                if (firstResult instanceof Node && firstResult.textContent) {
                    return firstResult.textContent;
                }
                else {
                    return firstResult.toString();
                }
            }
            else {
                return "Not found";
            }
        }
        else {
            return "Not found";
        }
    }
    matches(matcher, value) {
        if (matcher.equals !== undefined) {
            return matcher.equals === value;
        }
        if (matcher.contains !== undefined) {
            return value.includes(matcher.contains);
        }
        return false;
    }
    isApplicationMatched(eventDetails) {
        const appIdentifierResolved = this.configuredApp
            ?.appIdentifier;
        const matchIdentifier = appIdentifierResolved.find((identifier) => {
            const eventDetailsValue = eventDetails?.[identifier.type] || "";
            const matchParam = {
                [identifier.operator]: identifier.value,
            };
            return this.matches(matchParam, eventDetailsValue);
        });
        return matchIdentifier ? true : false;
    }
    async trackUrlChanges(event) {
        const appFound = this.isApplicationMatched(event.detail);
        if (appFound) {
            if (!this.appModel) {
                this.applicationEnds();
                try {
                    const appModel = await this.templateManager?.getTemplateByModleId(this.configuredApp?.id);
                    if (appModel) {
                        this.appModel = new ApplicationTemplate(appModel);
                        this.start();
                    }
                }
                catch (e) {
                    this.log.error("Tracking template not found");
                }
            }
        }
        else {
            this.applicationEnds();
        }
    }
    applicationEnds() {
        this.appModel = undefined;
        if (this.application) {
            this.clearAllViewTrackers();
            this.currentPageModel = undefined;
            this.application.trackEnd();
            this.application = undefined;
        }
    }
    isElementIntersect(domElement) {
        return new Promise((resolve) => {
            const o = new IntersectionObserver(([entry]) => {
                resolve(entry.intersectionRatio === 1);
                o.disconnect();
            });
            o.observe(domElement);
        });
    }
    isDocumentReady() {
        const outstandingRequests = () => {
            const entries = performance.getEntriesByType("resource");
            return entries.filter((entry) => entry.responseEnd === 0 && entry.renderBlockingStatus === "blocking");
        };
        return new Promise((resolved) => {
            const tid = setInterval(() => {
                if (document.readyState === "complete" &&
                    outstandingRequests().length === 0) {
                    resolved(true);
                }
                clearInterval(tid);
            }, 300);
        });
    }
    startTracking() {
        this.appUrlChangeObserver();
        this.pageElementEventObserver();
    }
    pageElementEventObserver() {
        document.addEventListener("click", ({ target }) => {
            if (target instanceof HTMLElement) {
                const viewEvent = this.viewEvents.find((event) => {
                    if (event.element.xpath) {
                        const result = first(query(event.element.xpath));
                        return result === target;
                    }
                });
                if (viewEvent) {
                    const details = this.getEventDetails(viewEvent, target);
                    const dataAttribute = [];
                    viewEvent.attributes &&
                        viewEvent.attributes.forEach((attribute) => {
                            dataAttribute.push(attribute.toString());
                        });
                    const viewTracker = this.trackerMap.get(viewEvent.pageKey);
                    //TODO complete the otehr attributes of a Event
                    viewTracker?.trackEvent(details.name, "click", details.attributes, details.tags);
                }
            }
            this.createViewsForDOMChanges();
        }, {
            passive: true,
            capture: true,
        });
    }
    appUrlChangeObserver() {
        this.urlObserver = new ParamHashPageObserver();
        this.urlObserver.addEventListener("urlchange", async (event) => this.trackUrlChanges(event));
        this.urlObserver?.start();
    }
}
class ApplicationTemplate {
    constructor(model) {
        this.name = model.name;
        this.identifier = model.identifier;
        this.pages = model.pages || [];
        this.views = model.views || [];
        this.startAttributes = model.startAttributes || [];
        this.endAttributes = model.startAttributes || [];
    }
}

/**
 * This is a stateless data service for communicating with the Analytics backend.
 *
 * It should not be part of the public api.
 */
class AnalyticsDataService {
    constructor(baseUrl, nonSPA) {
        this.baseUrl = baseUrl;
        this.nonSPA = nonSPA;
        this.log = Logger.create("DataService");
        this._baseUrl = this.baseUrl;
        if (this.nonSPA) {
            try {
                this.baseUrl =
                    sessionStorage.getItem(SDKConstants.YG_DS_BASE_URL) || undefined;
                this._baseUrl = this.baseUrl;
            }
            catch (err) {
                this.log.warn(`Failed find the baseUrl`, err);
            }
        }
    }
    streamUserActivity(request, accessToken) {
        return this.post("/yggdrasil/activity/stream", request, accessToken);
    }
    startSession(request, accessToken) {
        return this.post("/yggdrasil/activity/session", request, accessToken);
    }
    login(request) {
        return this.post("/yggdrasil/security/login", request);
    }
    refresh(request) {
        return this.post("/yggdrasil/security/refresh", request);
    }
    loadTemplate(request, accessToken) {
        return this.post("/yggdrasil/extension/template/load", request, accessToken);
    }
    setBaseUrl(baseUrl) {
        this._baseUrl = baseUrl;
        if (this.nonSPA) {
            sessionStorage.setItem(SDKConstants.YG_DS_BASE_URL, baseUrl);
        }
    }
    async post(url, data, accessToken) {
        const request = {
            method: "POST",
            body: JSON.stringify(data),
            headers: {
                "Content-Type": "application/json",
                "X-API-VERSION": "1",
            },
        };
        if (accessToken) {
            request.headers = {
                ...request.headers,
                Authorization: `Bearer ${accessToken}`,
            };
            request.keepalive = true; // make sure the request is sent even when the window is unloaded.
        }
        const response = await fetch(this.resolveUrl(url), request);
        if (!response.ok) {
            throw new HttpError(response);
        }
        const body = await response.json();
        return body;
    }
    resolveUrl(url) {
        if (url.match(/^https?:\/\//)) {
            return url;
        }
        const urlWithoutLeadingSlash = url.replace(/^\//, "");
        if (!this._baseUrl) {
            throw new Error("baseUrl is undefined in the AnalyticsDataService, " +
                "StandaloneTrackingClientConfig.baseUrl is depricated, set baseUrl attribute in the login response");
        }
        const baseWithoutTrailingSlash = this._baseUrl?.replace(/\/$/, "");
        return `${baseWithoutTrailingSlash}/${urlWithoutLeadingSlash}`;
    }
}
class HttpError extends Error {
    constructor(response) {
        super(`${response.status} ${response.statusText}`);
        this.headers = response.headers;
        this.statusCode = response.status;
        this.name = "HttpError";
    }
}

var name = "@yggdrasil/core";
var version = "0.5.7";
var packageInfo = {
	name: name,
	version: version
};

class StandaloneTrackingClient {
    constructor(config) {
        this.config = config;
        this.streamMap = new Map();
        this.streamHasChanges = false;
        this.visibilityObserver = new VisibilityObserver();
        this.log = Logger.create("StandaloneTrackingClient");
        this.oldStreamRequest = [];
        this.browserSupported = BrowserCompatibility.getInstance();
        this.browserSupported.setConfig(this.config);
        this.dataService = new AnalyticsDataService(config.baseUrl, config.nonSPA || false);
        this.reTryAfterDelay = 0;
        if (!this.browserSupported.isSafeToProceed()) {
            return;
        }
        validateConfig(config);
        this.updateConfigSettings();
        this.initSession();
        if (window === window.top || config.captureProxyMessages) {
            listenForProxyMessages(this);
        }
        this.visibilityObserver.observe();
        const tabGuid = sessionStorage.getItem(SDKConstants.YG_TC_TAB_GUID) ||
            generateGuid();
        sessionStorage.setItem(SDKConstants.YG_TC_TAB_GUID, tabGuid);
        this.tabGuid = tabGuid;
    }
    initSession() {
        this.session = this.startSession()
            .then((session) => {
            this.startPeriodicBatch();
            this.prepareForUnload();
            return session;
        })
            .catch((error) => {
            let messageStr = "Failed to start session";
            if (error instanceof HttpError) {
                messageStr = `${messageStr} (Session request failed with HTTP status ${error.statusCode})`;
                if (error.statusCode === 503 || error.statusCode === 408) {
                    const responseAfter = error.headers.get("Retry-After");
                    if (responseAfter) {
                        setTimeout(() => {
                            this.initSession();
                        }, Number(responseAfter) * 1000);
                    }
                }
            }
            const { message } = error;
            if (message.indexOf("baseUrl is undefined") > -1) {
                messageStr = `${messageStr}, ${message}`;
            }
            this.log.error(messageStr);
        });
    }
    trackApplicationStart(app, proxyClientReferanceId) {
        if (proxyClientReferanceId) {
            return;
        }
        validateApplication(app);
        const parentFound = this.getParentGuidByProxyClientRef(proxyClientReferanceId);
        this.streamMap.set(app.guid, {
            application: {
                hostname: app.hostname,
                identifier: app.identifier,
                name: app.name,
                version: app.version,
                state: {
                    start: {
                        commit: true,
                        timestamp: app.timestamp,
                        attributes: transformAttributes(app.attributes),
                    },
                },
                type: app.type,
                tags: transformTags(app.tags),
                guid: app.guid,
                parentGuid: app.parentGuid,
                features: transformTags(app.features),
                siteWorkflows: transformTags(app.siteWorkflows),
                globalWorkflows: transformTags(app.globalWorkflows),
            },
            viewMap: new Map(),
            trackingStrategy: app.trackingStrategy,
        });
        this.streamHasChanges = true;
    }
    trackApplicationEnd(app, proxyClientReferanceId) {
        if (proxyClientReferanceId) {
            return;
        }
        validateApplication(app);
        const stream = this.streamMap.get(app.guid);
        Assert.defined(stream, "Cannot end an application that isn't started");
        const { state } = stream.application;
        state.end = {
            focusDuration: this.focusedSeconds(state.start.timestamp, app.timestamp),
            timestamp: app.timestamp,
            attributes: transformAttributes(app.attributes),
            bounce: [...stream.viewMap.values()].find((viewEntry) => !viewEntry.bounceState)
                ? false
                : true,
        };
        this.streamHasChanges = true;
    }
    trackApplicationEvent(event, proxyClientReferanceId) {
        if (proxyClientReferanceId) {
            return;
        }
        validateApplicationEvent(event);
        const stream = this.streamMap.get(event.appGuid);
        Assert.defined(stream, "Cannot track an application event if the application isn't started");
        stream.applicationEvents = stream.applicationEvents ?? [];
        stream.applicationEvents.push({
            name: event.name,
            source: event.source,
            timestamp: event.timestamp,
            attributes: transformAttributes(event.attributes),
            type: event.type,
            tags: transformTags(event.tags),
            guid: event.guid,
            features: transformTags(event.features),
            siteWorkflows: transformTags(event.siteWorkflows),
            globalWorkflows: transformTags(event.globalWorkflows),
        });
        this.streamHasChanges = true;
    }
    trackViewStart(view, proxyClientReferanceId) {
        validateView(view);
        let stream = this.streamMap.get(view.appGuid);
        let parentView;
        if (proxyClientReferanceId) {
            parentView = this.getParentGuidByProxyClientRef(proxyClientReferanceId);
            if (parentView?.appKey) {
                stream = this.streamMap.get(parentView?.appKey);
            }
        }
        Assert.defined(stream, "Cannot track a view of an application that isn't started");
        stream.viewMap.set(view.guid, {
            name: view.name,
            url: view.url,
            state: {
                start: {
                    commit: true,
                    timestamp: view.timestamp,
                    attributes: transformAttributes(view.attributes),
                },
            },
            type: view.type,
            tags: transformTags(view.tags),
            guid: view.guid,
            parentGuid: view.parentGuid || parentView?.view.guid,
            features: transformTags(view.features),
            siteWorkflows: transformTags(view.siteWorkflows),
            globalWorkflows: transformTags(view.globalWorkflows),
            bounceState: true,
            proxyClientRef: view.proxyClientRef,
            proxyTag: proxyClientReferanceId,
        });
        this.streamHasChanges = true;
    }
    trackViewEnd(view, proxyClientReferanceId) {
        validateView(view);
        let stream = this.streamMap.get(view.appGuid);
        let parentView;
        if (proxyClientReferanceId) {
            parentView = this.getParentGuidByProxyClientRef(proxyClientReferanceId);
            if (parentView?.appKey) {
                stream = this.streamMap.get(parentView?.appKey);
            }
        }
        Assert.defined(stream, "Cannot track a view for an untracked application");
        const runningView = stream.viewMap.get(view.guid);
        Assert.defined(runningView, "Cannot end a view when the view isn't started");
        const { state } = runningView;
        if (runningView.proxyClientRef) {
            this.updateStatusOfChildViews(view.appGuid, view.timestamp, runningView.proxyClientRef, runningView.guid);
        }
        // child views bounce status
        const childBounceState = [...stream.viewMap.values()]
            .filter((x) => x.parentGuid === runningView.guid)
            .find((y) => !y.bounceState)
            ? false
            : true;
        state.end = {
            focusDuration: this.focusedSeconds(state.start.timestamp, view.timestamp),
            timestamp: view.timestamp,
            attributes: transformAttributes(view.attributes),
            bounce: runningView.bounceState &&
                childBounceState &&
                (view.bounce == false ? false : true),
        };
        this.streamHasChanges = true;
    }
    trackEvent(event, proxyClientRefId) {
        validateEvent(event);
        Assert.definedProperty(event, "appGuid");
        Assert.definedProperty(event, "viewGuid");
        let app = this.streamMap.get(event.appGuid);
        let parentView;
        if (proxyClientRefId) {
            parentView = this.getParentGuidByProxyClientRef(proxyClientRefId);
            if (parentView?.appKey) {
                app = this.streamMap.get(parentView?.appKey);
            }
        }
        Assert.defined(app, "Cannot track an event for an untracked application");
        const view = app.viewMap.get(event.viewGuid);
        Assert.defined(view, "Cannot track an event for an untracked view");
        view.events = view.events ?? [];
        view.events.push({
            name: event.name,
            timestamp: event.timestamp,
            type: event.type,
            attributes: transformAttributes(event.attributes),
            tags: transformTags(event.tags),
            guid: event.guid,
            features: transformTags(event.features),
            siteWorkflows: transformTags(event.siteWorkflows),
            globalWorkflows: transformTags(event.globalWorkflows),
            bounce: event.bounce,
        });
        view.bounceState = view.bounceState && (event.bounce || false);
        this.streamHasChanges = true;
    }
    trackContent(content, proxyClientRefId) {
        validateContent(content);
        let app = this.streamMap.get(content.appGuid);
        let parentView;
        if (proxyClientRefId) {
            parentView = this.getParentGuidByProxyClientRef(proxyClientRefId);
            if (parentView?.appKey) {
                app = this.streamMap.get(parentView?.appKey);
            }
        }
        Assert.defined(app, "Cannot track content for an untracked application");
        const view = app.viewMap.get(content.viewGuid);
        Assert.defined(view, "Cannot track content for an untracked view");
        view.items = view.items ?? [];
        view.items.push({
            name: content.name,
            timestamp: content.timestamp,
            type: content.type,
            attributes: transformAttributes(content.attributes),
            tags: transformTags(content.tags),
            guid: content.guid,
            features: transformTags(content.features),
            siteWorkflows: transformTags(content.siteWorkflows),
            globalWorkflows: transformTags(content.globalWorkflows),
        });
        this.streamHasChanges = true;
    }
    setSessionStore(sessionStoreage) {
        this.sessionStorage = sessionStoreage;
    }
    getConfig() {
        return {
            enableOnWebdriver: this.config.enableOnWebdriver,
            disableTracking: this.config.disableTracking,
        };
    }
    async loadTemplate(request) {
        // For prod testing when web security disabled
        const templateHost = localStorage.getItem(`${templatePrefix}__prod__host`);
        if (templateHost) {
            return await loadTemplatelocally(`${templateHost}/${request.identifier.logicalId || request.identifier.hostname}.json`);
        }
        await this.session;
        const accessToken = await this.getAccessToken();
        return await this.dataService.loadTemplate(request, accessToken);
    }
    focusedSeconds(startTimestamp, stopTimestamp) {
        const focusedTime = this.visibilityObserver.visibleTime(startTimestamp, stopTimestamp);
        return Math.floor(focusedTime / 1000);
    }
    startPeriodicBatch() {
        const INTERVAL_MS = this.config.schedule?.interval || 60 * 1000;
        const JITTER_MS = this.config.schedule?.jitter || 10 * 1000;
        const MAX_FAILED_BEFORE_STOP = 10;
        const randomDelay = () => new Promise((resolve) => {
            let waitTime = INTERVAL_MS + Math.floor(Math.random() * JITTER_MS - JITTER_MS / 2);
            if (this.reTryAfterDelay > 0) {
                waitTime = this.reTryAfterDelay;
            }
            setTimeout(resolve, waitTime);
        });
        let consecutiveFailedAttempts = 0;
        const sendAfterSomeTime = async () => {
            await randomDelay();
            try {
                if (this.streamHasChanges) {
                    await this.sendStreams();
                    this.streamHasChanges = false;
                    consecutiveFailedAttempts = 0;
                    this.reTryAfterDelay = 0;
                    this.oldStreamRequest = [];
                }
            }
            catch (err) {
                consecutiveFailedAttempts++;
                this.log.warn(`Failed to send analytics data to server`, err);
            }
            if (consecutiveFailedAttempts < MAX_FAILED_BEFORE_STOP) {
                sendAfterSomeTime();
            }
            else {
                this.streamMap.clear();
                this.streamHasChanges = false;
                this.log.error(`Stopping after ${consecutiveFailedAttempts} failed attempts to send analytics data.`);
            }
        };
        sendAfterSomeTime();
    }
    async sendStreams() {
        let accessToken;
        try {
            accessToken = await this.getAccessToken();
        }
        catch (err) {
            throw new Error("Failed to start session (Could not get access token)");
        }
        let streams = this.oldStreamRequest;
        if (this.reTryAfterDelay === 0) {
            streams = this.prepareStreams();
        }
        try {
            await this.dataService.streamUserActivity({ tabGuid: this.tabGuid, streams }, accessToken);
            if (this.sessionStorage) {
                await this.sessionStorage.clearOldViews();
                await this.sessionStorage.clearViewEvents();
                await this.sessionStorage.clearViewContents();
                await this.sessionStorage.removeEndedCurrentItems();
            }
        }
        catch (error) {
            if (error instanceof HttpError) {
                if (error.statusCode === 503 || error.statusCode === 408) {
                    this.oldStreamRequest = streams;
                    const responseAfter = error.headers.get("Retry-After") || "0";
                    this.reTryAfterDelay = Number(responseAfter) * 1000;
                }
            }
            let messageStr = "Stream endpoint failed";
            if (error instanceof HttpError) {
                messageStr = `${messageStr} (Stream request failed with HTTP status ${error.statusCode})`;
            }
            throw new Error(messageStr);
        }
    }
    async setSessionInStore(session) {
        // Add 10 min to session timestamp
        session.timestamp += 10 * 60 * 1000;
        sessionStorage.setItem(SDKConstants.YG_TC_LAST_SESSION, JSON.stringify(session));
    }
    async isSessionTimedout(session) {
        const storageSession = JSON.parse(sessionStorage.getItem(SDKConstants.YG_TC_LAST_SESSION) || "null");
        return storageSession && storageSession.timestamp >= session.timestamp;
    }
    async startSession() {
        let accessToken;
        try {
            accessToken = await this.getAccessToken();
        }
        catch (err) {
            throw new Error("Failed to start session (Could not get access token)");
        }
        const session = {
            timestamp: TrackingUtil.timestamp(),
        };
        const sessionConfig = this.config.session;
        if (sessionConfig) {
            session.client = sessionConfig.client;
            session.settings = sessionConfig.settings;
        }
        if (this.config.nonSPA) {
            const validSession = await this.isSessionTimedout(session);
            if (validSession) {
                return session;
            }
        }
        await this.dataService.startSession({ session }, accessToken);
        if (this.config.nonSPA) {
            await this.setSessionInStore(session);
        }
        return session;
    }
    prepareStreams() {
        const streams = [];
        for (const entry of this.streamMap.values()) {
            const views = entry.viewMap.size
                ? [...entry.viewMap.values()]
                : undefined;
            const stream = {
                application: entry.application,
                applicationEvents: entry.applicationEvents,
                views,
                trackingStrategy: entry.trackingStrategy,
            };
            streams.push(stream);
        }
        const streamsCopy = JSON.parse(JSON.stringify(streams));
        this.commitStreams();
        return streamsCopy.map((stramItem) => {
            stramItem.views?.map((view) => {
                delete view.bounceState;
                delete view.proxyClientRef;
                delete view.proxyTag;
                return { ...view };
            });
            return stramItem;
        });
    }
    commitStreams() {
        for (const [appId, appEntry] of this.streamMap.entries()) {
            appEntry.applicationEvents = undefined;
            if (appEntry.application.state.end) {
                this.streamMap.delete(appId);
            }
            else {
                appEntry.application.state.start.commit = false;
                for (const [viewId, viewEntry] of appEntry.viewMap.entries()) {
                    viewEntry.events = undefined;
                    viewEntry.items = undefined;
                    if (viewEntry.state.end) {
                        appEntry.viewMap.delete(viewId);
                    }
                    else {
                        viewEntry.state.start.commit = false;
                    }
                }
            }
        }
    }
    /**
     * Listens for the window "unload" event,
     * and send any available data.
     * All started applications/views will be ended after this.
     */
    prepareForUnload() {
        const unloadData = () => {
            try {
                const now = TrackingUtil.timestamp();
                for (const appEntry of this.streamMap.values()) {
                    if (!appEntry.application.state.end) {
                        for (const viewEntry of appEntry.viewMap.values()) {
                            if (!viewEntry.state.end) {
                                viewEntry.state.end = {
                                    focusDuration: this.focusedSeconds(viewEntry.state.start.timestamp, now),
                                    timestamp: now,
                                };
                            }
                        }
                        appEntry.application.state.end = {
                            focusDuration: this.focusedSeconds(appEntry.application.state.start.timestamp, now),
                            timestamp: now,
                        };
                    }
                }
            }
            finally {
                this.sendStreams();
            }
        };
        addEventListener("unload", unloadData);
    }
    async getAccessToken() {
        if (!this.tokenManager) {
            this.tokenManager = this.initTokenManager();
        }
        return this.tokenManager.getAccessToken();
    }
    initTokenManager() {
        return new TokenManager(this.dataService, this.config.auth, this.config.nonSPA || false);
    }
    updateConfigSettings() {
        const { session } = this.config;
        if (session) {
            const { settings } = session;
            if (!settings) {
                this.config.session = {
                    ...this.config.session,
                    settings: { locales: [], languages: [] },
                };
            }
        }
        else {
            this.config = {
                ...this.config,
                session: { settings: { locales: [], languages: [] } },
            };
        }
        const languages = this.config.session?.settings?.languages || [];
        const locales = this.config.session?.settings?.locales || [];
        //set client library wrapper and version
        if (this.config.session?.settings) {
            this.config.session.settings.yggdrasilVersion = packageInfo.version;
            this.config.session.settings.yggdrasilLibrary =
                window.ygLibraryWrapper || "JAVA_SCRIPT";
            if (window.ygLibraryWrapperVersion &&
                window.ygLibraryWrapperVersion !== packageInfo.version) {
                this.config.session.settings.yggdrasilVersion = `${packageInfo.version}_${window.ygLibraryWrapperVersion}`;
            }
        }
        const urlSearchParams = new URLSearchParams(window.location.href);
        // Infor OS application uses `inforCurrentLocale` to set the locale
        const appUrllocale = urlSearchParams.get("inforCurrentLocale");
        const appLocaleObject = appUrllocale &&
            appUrllocale !== null && { id: appUrllocale, weight: 1 };
        if (appLocaleObject) {
            const isLocaleExist = locales.find((item) => item.id === appUrllocale);
            if (!isLocaleExist) {
                locales.push(appLocaleObject);
            }
        }
        // Infor OS application uses `inforCurrentLanguage` to set the language
        const appUrlLang = urlSearchParams.get("inforCurrentLanguage");
        const appLangObject = appUrlLang &&
            appUrlLang !== null && { id: appUrlLang, weight: 1 };
        if (appLangObject) {
            const isLangExist = languages.find((item) => item.id === appUrlLang);
            if (!isLangExist) {
                languages.push(appLangObject);
            }
        }
    }
    getParentGuidByProxyClientRef(proxyClientRef) {
        if (!proxyClientRef) {
            return;
        }
        for (const [key, entry] of this.streamMap.entries()) {
            const views = entry.viewMap.size
                ? [...entry.viewMap.values()]
                : undefined;
            const foundView = views?.find((view) => view.proxyClientRef === proxyClientRef);
            if (foundView) {
                return {
                    appKey: key,
                    view: foundView,
                };
            }
        }
        return;
    }
    updateStatusOfChildViews(appGuid, timestamp, proxyClientRef, guid) {
        if (proxyClientRef && guid) {
            const application = this.streamMap.get(appGuid);
            if (application) {
                const appViews = [...application.viewMap.values()];
                appViews
                    .filter((entry) => entry.proxyTag === proxyClientRef && entry.parentGuid === guid)
                    .forEach((runingChildview) => {
                    const { state } = runingChildview;
                    if (runingChildview.proxyClientRef) {
                        this.updateStatusOfChildViews(appGuid, timestamp, runingChildview.proxyClientRef, runingChildview.guid);
                    }
                    // child views bounce status
                    const childBounceState = appViews
                        .filter((x) => x.parentGuid === runingChildview.guid)
                        .find((y) => !y.bounceState)
                        ? false
                        : true;
                    state.end = {
                        focusDuration: this.focusedSeconds(state.start.timestamp, timestamp),
                        timestamp: timestamp,
                        bounce: runingChildview.bounceState && childBounceState,
                    };
                });
            }
        }
    }
}
/**
 * Maps client-side Attributes (a record) to an array of {key, value} objects, as accepted by the data service.
 */
function transformAttributes(attributes) {
    if (!attributes || Object.keys(attributes).length === 0) {
        return undefined;
    }
    const result = [];
    for (const [key, value] of Object.entries(attributes)) {
        if (value !== null && value !== undefined) {
            result.push({
                key,
                value: String(value),
            });
        }
    }
    return result;
}
function transformTags(tags) {
    if (tags && tags.length > 0) {
        return tags;
    }
    else {
        return undefined;
    }
}
class TokenManager {
    constructor(dataService, auth, nonSPA) {
        this.dataService = dataService;
        this.auth = auth;
        this.nonSPA = nonSPA;
        this.storage = new InMemoryTokenStorage(this.nonSPA);
        this.log = Logger.create("TokenManager");
    }
    async getAccessToken() {
        if (!this.storage.hasToken()) {
            await this.login();
        }
        if (this.expired()) {
            try {
                await this.refresh();
            }
            catch (err) {
                // TODO: Catch specific error, e.g "RefreshTokenExpired", "RefreshTokenInvalid"
                await this.login();
            }
        }
        return this.storage.getToken().accessToken;
    }
    async refresh() {
        const old = this.storage.getToken();
        const refreshedToken = await this.dataService.refresh({
            grantType: "refreshToken",
            refreshToken: old.refreshToken,
        });
        this.storage.setToken({
            accessToken: refreshedToken.accessToken,
            refreshToken: refreshedToken.refreshToken || old.refreshToken,
            expiryTime: this.calculateExpiryTime(refreshedToken.expiresIn),
        });
    }
    async login() {
        let newToken;
        switch (this.auth.type) {
            /**
             * @deprecated
             */
            case "credentials":
                newToken = await this.dataService.login({
                    tracker: {
                        sessionGuid: this.auth.sessionGuid,
                        userGuid: this.auth.userGuid,
                        tenantId: this.auth.tenantId,
                    },
                    credentials: {
                        siteName: this.auth.siteName,
                        siteSecret: this.auth.siteSecret,
                    },
                });
                break;
            case "custom":
                newToken = await this.auth.login();
                if (!newToken.baseUrl) {
                    this.log.warn("baseUrl not found in the login response, StandaloneTrackingClientConfig.baseUrl is depricated, " +
                        "set the baseUrl attribute in the login response.");
                }
                else {
                    this.dataService.setBaseUrl(newToken.baseUrl);
                }
                break;
        }
        this.storage.setToken({
            accessToken: newToken.accessToken,
            refreshToken: newToken.refreshToken,
            expiryTime: this.calculateExpiryTime(newToken.expiresIn),
        });
    }
    expired() {
        const token = this.storage.getToken();
        const now = Date.now();
        return token.expiryTime <= now;
    }
    calculateExpiryTime(expiresInSeconds) {
        return Date.now() + expiresInSeconds * 1000;
    }
}
class InMemoryTokenStorage {
    constructor(nonSPA) {
        this.nonSPA = nonSPA;
    }
    getToken() {
        if (!this.token) {
            throw new TypeError("getToken() was called without first checking hasToken()");
        }
        return this.token;
    }
    setToken(token) {
        this.token = token;
        if (this.nonSPA) {
            sessionStorage.setItem(SDKConstants.YG_TC_ACCESS_TOKEN, JSON.stringify(token));
        }
    }
    hasToken() {
        if (!this.token) {
            if (this.nonSPA) {
                this.token = JSON.parse(sessionStorage.getItem(SDKConstants.YG_TC_ACCESS_TOKEN) || "null");
            }
        }
        return !!this.token;
    }
    clear() {
        this.token = undefined;
    }
}
function validateConfig(config) {
    class InvalidConfigError extends Error {
        constructor() {
            super(...arguments);
            this.name = "InvalidConfigError";
        }
    }
    try {
        Assert.defined(config.auth, "auth config must be set");
        if (config.auth.type === "credentials") {
            Assert.isGuid(config.auth.sessionGuid);
            Assert.isGuid(config.auth.userGuid);
            Assert.nonEmptyString(config.auth.siteName, "siteName must be a non-empty string");
            Assert.nonEmptyString(config.auth.siteSecret, "siteSecret must be a non-empty string");
            if (isDefined(config.auth.tenantId)) {
                if (isDefined(config.auth.tenantId)) {
                    Assert.stringWithLength(config.auth.tenantId, { min: 1, max: 256 }, `tenantId must be a string with 1 to 256 characters, got ${config.auth.tenantId}`);
                }
            }
        }
        if (isDefined(config.session)) {
            if (isDefined(config.session.client)) {
                Assert.stringWithLength(config.session.client, { min: 1, max: 256 }, `session.client must be a string with 1 to 256 characters, got ${config.session.client}`);
            }
            if (isDefined(config.session.settings)) {
                if (isDefined(config.session.settings.locales) &&
                    config.session.settings.locales.length > 0) {
                    config.session.settings.locales.forEach((locale, i) => {
                        if (isDefined(locale.id)) {
                            Assert.stringWithLength(locale.id, { min: 2, max: 10 }, `config.session.settings.locales[${i}].id must be a string with 2 to 10 characters, got ${locale.id}`);
                        }
                        if (isDefined(locale.weight)) {
                            Assert.positiveInteger(locale.weight, `config.session.settings.locales[${i}].weight must be a positive integer, got ${locale.weight}`);
                        }
                    });
                }
                if (isDefined(config.session.settings.languages) &&
                    config.session.settings.languages.length > 0) {
                    config.session.settings.languages.forEach((language, i) => {
                        if (isDefined(language.id)) {
                            Assert.stringWithLength(language.id, { min: 2, max: 10 }, `config.session.settings.locales[${i}].id must be a string with 2 to 10 characters, got ${language.id}`);
                        }
                        if (isDefined(language.weight)) {
                            Assert.positiveInteger(language.weight, `config.session.settings.locales[${i}].weight must be a positive integer, got ${language.weight}`);
                        }
                    });
                }
            }
        }
        if (isDefined(config.schedule)) {
            Assert.positiveInteger(config.schedule.interval);
            Assert.true(config.schedule.interval !== 0, "schedule.interval cannot be 0");
            Assert.positiveInteger(config.schedule.jitter);
            Assert.true(config.schedule.jitter !== 0, "schedule.jitter cannot be 0");
        }
    }
    catch (error) {
        if (error instanceof Error) {
            throw new InvalidConfigError(error.message);
        }
        else {
            throw error;
        }
    }
    function isDefined(value) {
        return value !== null && value !== undefined;
    }
}

/*
 * Public API Surface of analytics
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ApplicationTracker, AutoApplicationTracker, DevTrackingClient, Logger, NonSinglePageApplicationTracker, PageSessionStorage, PlatformApplicationTracker, ProxyTrackingClient, StandaloneTrackingClient, TemplateApplicationTracker, TrackingUtil, ViewTracker, Assert as _Assert, generateGuid, setLogLevel, startAutoTracking, startTemplateTracking };
